/**
 * The machine's account of the message in flight, and who repaints when it changes.
 *
 * This file used to also hold the client-side pane capture (capture-scrollback text,
 * the coalesced pokePane trigger) that fed the client extractors. That whole layer
 * moved to the machine on 2026-08-30 (edge paneLive.ts): the pane, the transcript
 * evidence and the turn accumulator live together where they are consistent, and a
 * resubscribe replays the current text — the queue the owner asked for. What remains
 * here is the last `live-text` frame, keyed to one conversation.
 */
let pushed: string | null = null
let pushedKind: string | null = null
let key = ''
let version = 0
const listeners = new Set<() => void>()

/** Point the store at a conversation. A different key drops the previous pane's
 *  text so it can never be read as this one's.
 *
 *  RENAMED from `usePaneFor` (2026-09-02): it is a plain module-store setter, not a
 *  React hook, and the `use` prefix was a lie the first linter this repo ever ran
 *  called out on its first pass — rules-of-hooks flagged the call site inside an
 *  effect as "a hook in a callback". A function named like a hook invites hook rules
 *  onto a plain function, and plain-function habits onto real hooks. */
export function pointPaneAt(sessionKey: string): void {
  if (key === sessionKey) return
  key = sessionKey
  pushed = null
  pushedKind = null
  version++
  notify()
}

/** The machine's own account of the message in flight, or null. */
export function pushedLiveText(): string | null {
  return pushed
}

export function setPushedLiveText(text: string, kind?: string): void {
  const next = text.trim() ? text : null
  const k = next ? (kind ?? null) : null
  if (next === pushed && k === pushedKind) return
  pushed = next
  pushedKind = k
  version++
  notify()
}

/** What the pushed text IS — 'thinking' for agy, absent for a plain reply. */
export function pushedLiveKind(): string | null {
  return pushedKind
}

/** The snapshot `useSyncExternalStore` compares. */
export function paneVersion(): number {
  return version
}

export function subscribePane(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Drop everything — leaving the conversation, or signing out. */
export function resetPane(): void {
  pushed = null
  pushedKind = null
  key = ''
  version++
  notify()
}

if (typeof window !== 'undefined') {
  ;(window as unknown as { __livePane?: unknown }).__livePane = {
    text: () => pushed,
    version: () => version,
  }
}

function notify(): void {
  for (const cb of [...listeners]) {
    try {
      cb()
    } catch {
      // A subscriber that throws must not stop the others.
    }
  }
}
